<template>
  <div class="chargeback-price-config">
    <v-row>
      <v-col cols="12" sm="6" xl="3">
        <v-skeleton-loader v-if="isLoading" type="card,list-item-two-line" />

        <v-card v-else>
          <v-card-title>Price Grid</v-card-title>

          <v-divider />

          <v-card-text class="d-flex align-center chargeback-price-config-list text-subtitle-1 font-weight-light">
            <p>Recalculate Chargeback Event Prices for Merchants using Price Grid</p>
          </v-card-text>

          <v-card-actions class="justify-center">
            <v-btn @click="recalculateChargebackPrices()" color="success">Recalculate</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" xl="3">
        <v-skeleton-loader v-if="isLoading" type="card,list-item-two-line" />

        <v-card v-else-if="ethocaChargebackEventPrices">
          <v-card-title>MasterCard Alert Price</v-card-title>

          <v-divider />

          <ChargebackPriceConfigList :chargeback-event-prices="ethocaChargebackEventPrices" />

          <v-card-actions class="justify-center">
            <v-btn
              @click="openPriceConfigModal(priceConfigServiceType.ETHOCA, ethocaChargebackEventPrices)"
              color="success"
            >
              Change MasterCard Alert Price
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" xl="3">
        <v-skeleton-loader v-if="isLoading" type="card,list-item-two-line" />

        <v-card v-else-if="visaCdrnChargebackEventPrices">
          <v-card-title>Visa CDRN Alert Price</v-card-title>

          <v-divider />

          <ChargebackPriceConfigList :chargeback-event-prices="visaCdrnChargebackEventPrices" />

          <v-card-actions class="justify-center">
            <v-btn
              @click="openPriceConfigModal(priceConfigServiceType.VISA_CDRN, visaCdrnChargebackEventPrices)"
              color="success"
            >
              Change Visa CDRN Alert Price
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" xl="3">
        <v-skeleton-loader v-if="isLoading" type="card,list-item-two-line" />

        <v-card v-else-if="visaRdrChargebackEventPrices">
          <v-card-title>Visa RDR Case Prices</v-card-title>

          <v-divider />

          <ChargebackPriceConfigList :chargeback-event-prices="visaRdrChargebackEventPrices" />

          <v-card-actions class="justify-center">
            <v-btn
              @click="openPriceConfigModal(priceConfigServiceType.VISA_RDR, visaRdrChargebackEventPrices)"
              color="success"
            >
              Change Visa RDR Case Prices
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <ChargebackPriceConfigModal @closePriceModal="closePriceModal" />
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api';
import store from '@/store';
import { eventBus } from '@/main';
import { required } from '@core/utils/validation';
import PriceConfigServiceType from '@/enums/report-service-type.enum';
import ChargebackPriceConfigList
  from '@/views/chargebacks/chargeback-price-configs/_partials/ChargebackPriceConfigList.vue';
import ChargebackPriceConfigModal
  from '@/views/chargebacks/chargeback-price-configs/components/ChargebackPriceConfigModal.vue';
import Vue from 'vue';

export default {
  components: { ChargebackPriceConfigModal, ChargebackPriceConfigList },
  setup() {
    const priceConfigByType = {
      [PriceConfigServiceType.ETHOCA]: ref(null),
      [PriceConfigServiceType.VISA_CDRN]: ref(null),
      [PriceConfigServiceType.VISA_RDR]: ref(null),
    };

    const isLoading = ref(true);

    const visaRdrChargebackEventPrices = computed(() => priceConfigByType[PriceConfigServiceType.VISA_RDR].value);
    const visaCdrnChargebackEventPrices = computed(() => priceConfigByType[PriceConfigServiceType.VISA_CDRN].value);
    const ethocaChargebackEventPrices = computed(() => priceConfigByType[PriceConfigServiceType.ETHOCA].value);

    const priceConfigServiceType = PriceConfigServiceType;

    const openPriceConfigModal = (type, prices) => {
      eventBus.$emit('chargeback-price-config.modal.show', ('chargeback-price-config.modal.show', {
        type: type,
        prices: prices.map(price =>  { return { price: price.price, tier: price.tier }}),
      }));
    };

    const recalculateChargebackPrices = () => {
      Vue.$confirm({
        message: 'Are you sure want to run the Chargeback Events prices recalculation process for all merchants with enabled Price Grid?',
        button: {
          no: 'No',
          yes: 'Yes',
        },
        callback: confirm => {
          if (confirm) {
            store.dispatch('companyPrice/recalculateChargebackPrices').then(() => {
              Vue.notify({
                type: 'success',
                title: 'Recalculate Chargeback Prices',
                text: 'Recalculation is finished, please see Slack log channel for more details',
                duration: 10000,
              });
            }).catch((error) => {
              Vue.notify({
                type: 'error',
                title: 'Recalculate Chargeback Prices',
                text: `Recalculation failed, reason: ${error.message}. Please see Slack log channel for more details`,
              });
            });
          }
        },
      });
    };

    store.dispatch('chargebackPriceConfig/fetchEventPriceConfig').then(chargebackEventPriceConfig => {
      if (chargebackEventPriceConfig.length > 0) {
        Object.keys(priceConfigByType).forEach(type => {
          priceConfigByType[type].value = chargebackEventPriceConfig.filter(
            price => price.serviceType === type,
          );
        });
      }
    })
      .catch(() => {
        Vue.notify({
          type: 'error',
          title: 'Failed to load price configuration.',
          duration: 5000,
        });
      })
      .finally(() => isLoading.value = false);

    const findPriceConfigIndexByTier = (prices, tier) => {
      return prices.findIndex(price => price.tier === tier);
    };

    const closePriceModal = (data) => {
      data.prices.forEach((price) => {
        const priceConfig = priceConfigByType[data.type];
        if (priceConfig) {
          priceConfig.value[findPriceConfigIndexByTier(priceConfig.value, price.tier)].price = price.price;
        }
      });
    };

    return {
      isLoading,
      visaRdrChargebackEventPrices,
      visaCdrnChargebackEventPrices,
      ethocaChargebackEventPrices,
      openPriceConfigModal,
      recalculateChargebackPrices,
      priceConfigServiceType,
      closePriceModal,
      validators: { required },
    };
  },
};
</script>

<style lang="scss" scoped>
.chargeback-price-config {
  ::v-deep .v-btn {
    max-width: 100%;
  }
  ::v-deep .v-btn__content {
    white-space: pre-wrap;
    max-width: 100%;
  }
}

.chargeback-price-config-list {
  min-height: 10rem;
}
</style>
